import { SessionExpiredError } from "@/common/errors/session-expired-error";
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import i18next from "i18next";
import qs from "qs";
import { v4 as uuidv4 } from "uuid";
import { config } from "./configuration";
import { CORRELATION_ID_HEADER, getRequestCorrelationId } from "./correlation-id";
import { isSessionRecoveryMsalError } from "./msal-error";
import Sentry from "./sentry";

type TokenProvider = () => Promise<{ apiToken: string; msGraphToken: string }>;
type TokenSelector = (tokens: { apiToken: string; msGraphToken: string }) => string;

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let tokenProvider: TokenProvider | null = null;

const setTokenProvider = (provider: TokenProvider): void => {
  tokenProvider = provider;
};

const getTokenProvider = (): TokenProvider | null => {
  return tokenProvider;
};

const clearTokenProvider = (): void => {
  tokenProvider = null;
};

const updateAxiosHeaders = (apiToken: string, msGraphToken: string): void => {
  alphartisApiAxios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  microsoftGraphAxios.defaults.headers.common.Authorization = `Bearer ${msGraphToken}`;
};

const clearAxiosHeaders = (): void => {
  delete alphartisApiAxios.defaults.headers.common.Authorization;
  delete microsoftGraphAxios.defaults.headers.common.Authorization;
  clearTokenProvider();
};

const acquireBearerToken = async (
  getToken: TokenSelector,
  sentryAction: string,
  requestUrl?: string,
  requestMethod?: string,
): Promise<string | null> => {
  if (!tokenProvider) {
    return null;
  }

  try {
    return getToken(await tokenProvider());
  } catch (error) {
    if (isSessionRecoveryMsalError(error)) {
      clearAxiosHeaders();
      throw new SessionExpiredError();
    }

    Sentry.captureException(error, {
      extra: {
        action: sentryAction,
        requestUrl,
        requestMethod,
      },
    });
    throw error;
  }
};

const applyBearerToken = async (
  requestConfig: InternalAxiosRequestConfig,
  getToken: TokenSelector,
  sentryAction: string,
): Promise<InternalAxiosRequestConfig> => {
  const token = await acquireBearerToken(getToken, sentryAction, requestConfig.url, requestConfig.method);

  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }

  return requestConfig;
};

const createResponseInterceptor = (axiosInstance: typeof alphartisApiAxios) => {
  return axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;

      if (error.response?.status === 401 && tokenProvider && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const { apiToken, msGraphToken } = await tokenProvider();

          if (axiosInstance === alphartisApiAxios) {
            originalRequest.headers.Authorization = `Bearer ${apiToken}`;
          } else if (axiosInstance === microsoftGraphAxios) {
            originalRequest.headers.Authorization = `Bearer ${msGraphToken}`;
          }

          return axiosInstance(originalRequest);
        } catch {
          clearAxiosHeaders();
          throw new SessionExpiredError();
        }
      }

      if (error.response?.status === 401) {
        clearAxiosHeaders();
        throw new SessionExpiredError();
      }

      return Promise.reject(error);
    },
  );
};

const alphartisApiAxios = axios.create({
  baseURL: config.apiEndpoint,
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "comma" }),
});

const microsoftGraphAxios = axios.create({
  baseURL: "https://graph.microsoft.com",
});

alphartisApiAxios.interceptors.request.use(
  async (requestConfig) => {
    // One ID per request: the 401 retry passes the same config through here again and keeps it.
    const correlationId = getRequestCorrelationId(requestConfig) ?? uuidv4();
    requestConfig.headers.set(CORRELATION_ID_HEADER, correlationId);
    requestConfig.headers.set("Accept-Language", i18next.resolvedLanguage ?? i18next.language ?? "de");

    return applyBearerToken(requestConfig, (tokens) => tokens.apiToken, "acquire-api-token-failed");
  },
  (error) => Promise.reject(error),
);

microsoftGraphAxios.interceptors.request.use(
  async (requestConfig) => {
    return applyBearerToken(requestConfig, (tokens) => tokens.msGraphToken, "acquire-msgraph-token-failed");
  },
  (error) => Promise.reject(error),
);

createResponseInterceptor(alphartisApiAxios);
createResponseInterceptor(microsoftGraphAxios);

export const fetcher = <T, R>(url: string) => alphartisApiAxios.get<T, AxiosResponse<R>>(url).then((res) => res.data);

export const urlFetcher = <T, R>(url: string, { arg }: { arg: string }) =>
  alphartisApiAxios.get<T, AxiosResponse<R>>(`${url}${arg}`).then((res) => res.data);

export const poster = <T, R>(url: string, { arg }: { arg: T }) =>
  alphartisApiAxios.post<T, AxiosResponse<R>>(url, arg).then((res) => res.data);

export const patcher = <T, R>(url: string, { arg }: { arg: T }) =>
  alphartisApiAxios.patch<T, AxiosResponse<R>>(url, arg).then((res) => res.data);

export const deleter = <T, R>(url: string, { arg }: { arg: string }) =>
  alphartisApiAxios.delete<T, AxiosResponse<R>>(`${url}/${arg}`).then((res) => res.data);

export {
  alphartisApiAxios,
  clearAxiosHeaders,
  getTokenProvider,
  microsoftGraphAxios,
  setTokenProvider,
  updateAxiosHeaders,
};
